import { useEffect, type RefObject } from 'react';

/**
 * Selector for elements that are focusable in a meaningful way for a focus
 * trap. We exclude `tabindex="-1"` because programmatic focus is fine but
 * keyboard Tab traversal should skip those nodes.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // jsdom doesn't compute layout, so don't gate on offsetParent in tests.
    return true;
  });
}

/**
 * Trap keyboard focus inside `ref` while `active` is true.
 *
 * - On activate: focuses the first focusable element inside the container
 *   (unless an element inside is already focused).
 * - On Tab / Shift+Tab: cycles focus within the container.
 * - On deactivate: restores focus to whatever was focused before activation.
 *
 * Designed for modal-style surfaces (BottomSheet, dialogs) where the rest
 * of the page is visually still present but should not receive focus.
 */
export function useFocusTrap(
  active: boolean,
  ref: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const previouslyFocused =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;

    // Move focus into the trap on activation if focus isn't already inside.
    const focusables = getFocusable(container);
    const alreadyInside = container.contains(document.activeElement);
    if (!alreadyInside) {
      const target = focusables[0] ?? container;
      // If the container itself isn't focusable, give it a synthetic
      // tabindex so keydown handlers fire even with no inner focusables.
      if (!focusables.length && !container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      // `preventScroll: true` keeps the browser from auto-scrolling the
      // newly-focused element into view, which would shift the map (and
      // everything else) when a bottom sheet rises from off-screen.
      target.focus({ preventScroll: true });
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const root = ref.current;
      if (!root) return;
      const items = getFocusable(root);
      if (items.length === 0) {
        // Nothing inside to cycle to — keep focus pinned to the container.
        event.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (current === first || !root.contains(current)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else {
        if (current === last || !root.contains(current)) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    }

    document.addEventListener('keydown', handleKey, true);

    return () => {
      document.removeEventListener('keydown', handleKey, true);
      // Restore focus only if the previously-focused element is still in the
      // DOM and focusable. Wrapped in try/catch because focus() can throw on
      // some detached nodes in older browsers.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch {
          /* noop */
        }
      }
    };
  }, [active, ref]);
}
